import { fixedTons } from "./common";

export const generateBarChartDataSet = (labels, data, colors) => {
  if (data.length !== labels.length || data.length !== colors.length) {
    throw new Error(
      "Labels, data, and colors arrays must have the same length"
    );
  }
  return labels.map((label, index) => ({
    label,
    data: data[index],
    backgroundColor: colors[index].background,
    borderColor: colors[index].border,
    borderWidth: 0.5,
  }));
};

const getColorForCategory = (categoryType, colors) => {
  const defaultColors = [
    { label: "Wastewater", color: "rgba(255, 187, 51, 0.8)" },
    { label: "Hazardous Waste", color: "rgba(0, 102, 231, 0.8)" },
    { label: "Non-Hazardous Waste", color: "rgba(0, 255, 0, 0.8)" },
    {
      label: "Construction & Demolition Waste",
      color: "rgba(255, 99, 132, 0.8)",
    },
  ];

  const colorsMap = colors || defaultColors;
  return (
    colorsMap.find((c) => c.label === categoryType)?.color ||
    "rgba(0, 0, 0, 0.1)"
  );
};

const monthRowsHaveYearMonth = (rows) =>
  (rows || []).some(
    (pt) =>
      Number.isFinite(Number(pt?.year)) && Number.isFinite(Number(pt?.month))
  );

export const manipulateDataForBarChart = ({
  data,
  colors,
  type,
  fromDate,
  toDate,
}) => {
  const wasteFacilityCategory = (item) =>
    item?.categoryType || item?.category || item?.wasteCategory;

  const toFiniteNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  return (
    data &&
    data?.map((item) => {
      const color = getColorForCategory(
        type === "vendorPurchase" || type === "vendorPurchaseEmission"
          ? item?.vendor
          : type === "purchaseEmission" ||
              type === "employeeCommuteEmission" ||
              type === "purchaseEmissionData" ||
              type === "employeeCommuteTransport" ||
              type === "travelmode" ||
              type === "vendoranalysis"
            ? item._id
            : type === "waste" || type === "facility"
              ? wasteFacilityCategory(item)
              : type === "discharge" || type === "withdrawal"
                ? item?.source
                : type === "transportationEmission"
                  ? item?.emission
                  : item?.categoryType,
        colors
      );

      const monthRows = Array.isArray(item?.data) ? item.data : [];
      const useAlignedMonths =
        fromDate &&
        toDate &&
        monthRowsHaveYearMonth(monthRows);

      return {
        label:
          type === "vendorPurchase" || type === "vendorPurchaseEmission"
            ? item?.vendor
            : type === "purchaseEmission" ||
                type === "purchaseEmissionData" ||
                type === "employeeCommuteEmission" ||
                type === "employeeCommuteTransport" ||
                type === "travelmode" ||
                type === "vendoranalysis"
              ? item._id
              : type === "discharge" || type === "withdrawal"
                ? item?.source
                : type === "transportationEmission"
                  ? item?.emission
                  : type === "waste" || type === "facility"
                    ? wasteFacilityCategory(item)
                    : item?.categoryType,
        data:
          type === "transportationEmission"
            ? (Array.isArray(item?.twelveMonthData) ? item.twelveMonthData : [])
                .map((month) => month?.totalEmission)
                .map((v) => toFiniteNumber(v))
            : useAlignedMonths
              ? buildAlignedMonthlySeries(monthRows, fromDate, toDate)
              : monthRows.map((month) =>
                type === "commute" ||
                type === "vendorPurchase" ||
                type === "category" ||
                type === "purchaseEmissionData"
                  ? toFiniteNumber(month?.totalSpent)
                  : type === "vendorPurchaseEmission" ||
                      type === "employeeCommuteTransport" ||
                      type === "employeeCommuteEmission" ||
                      type === "travelmode" ||
                      type === "vendoranalysis"
                    ? fixedTons(month?.totalEmission)
                    : type === "purchaseEmission" ||
                        type === "waste" ||
                        type === "facility" ||
                        type === "discharge" ||
                        type === "withdrawal" ||
                        type === "businessTravelEmission"
                      ? toFiniteNumber(
                          month?.total ?? month?.totalWaste ?? month?.totalSpent
                        )
                      : toFiniteNumber(month?.total) * 1000
              ),
        backgroundColor: color,
        borderColor: color.replace("0.8", "1"),
        borderWidth: 0.5,
      };
    })
  );
};

export const generateBarChartDataSetForWaste = (data, colors, label) => {
  let combinedData = [];
  if (label === "Waste Management") {
    combinedData = data?.map((item) => item?.totalWasteManaged);
  } else if (label === "Temporal Analysis") {
    combinedData = data?.map((item) => item?.totalQuantityInTons * 1000);
  } else if (label === "Commute") {
    combinedData = data?.map((item) => item?.totalEmission);
  } else if (label === "Trips") {
    combinedData = data?.map((item) => item?.numberOfTrips);
  } else if (label === "Employee Trips") {
    combinedData = data?.map((item) => item?.numberOfEmployees);
  } else if (label === "Fuel Efficiency") {
    combinedData = data?.map((item) => item?.averageFuelEfficiency);
  } else if (label === "Vendor Emission" || label === "Vendor Spent") {
    combinedData = data?.map((item) =>item?.totalSpent);
  } else if (label === "Facility Emission") {
    combinedData = data?.map((item) => item?.totalEmission);
  } else if (label === "Facility Spent") {
    combinedData = data?.map((item) => item?.amountSpend);
  } else if (label === "Activity Spend") {
    combinedData = data?.map((item) => item?.totalSpent);
  } else if (label === "Average Distance") {
    combinedData = data?.map((item) => item?.avgDistance);
  } else if (label === "Trip Frequency") {
    combinedData = data?.map((item) => item?.total);
  } else if (label === "Days") {
    combinedData = data?.map((item) => item?.totalDays);
  } else if (label === "Position") {
    combinedData = data?.map((item) => item?.total);
  } else if (
    label === "Production Materials" ||
    label === "Finished Goods" ||
    label === "Transportation and Distribution" ||
    label === "Information Technology and Communication" ||
    label === "Professional Services" ||
    label === "Others"
  ) {
    combinedData = data?.map((item) => item?.totalSpent);
  } else if (label === "Waste Generated") {
    combinedData = data?.map((item) => item?.totalWaste);
  } else if (label === "Waste Recycled") {
    combinedData = data?.map((item) =>
      Number(item?.quantityOfWasteManaged?.value ?? item?.totalWaste ?? 0)
    );
  } else {
    combinedData = data?.map((item) => item?.totalWaste * 1000);
  }
  const defaultColor = colors || "rgba(0, 0, 0, 0.8)";
  const normalizedData = (combinedData ?? []).map((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  });
  return [
    {
      label: label || "Waste Generated",
      data: normalizedData,
      backgroundColor: defaultColor,
      borderColor: defaultColor.replace("0.8", "1"),
      borderWidth: 0.5,
    },
  ];
};

/** Vendor waste-managed API may return `{ data: [...] }` or the series array directly. */
export const normalizeVendorWasteSeries = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/** Map API `{ year, month, total }` points onto the dashboard period filter (keeps zeros). */
export const buildAlignedMonthlySeries = (monthlyPoints, fromDate, toDate) => {
  const byKey = new Map();
  (monthlyPoints || []).forEach((pt) => {
    const y = Number(pt?.year);
    const m = Number(pt?.month);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    const raw = pt?.total ?? pt?.totalWaste ?? pt?.totalEmission ?? 0;
    const n = Number(raw);
    byKey.set(`${y}-${m}`, Number.isFinite(n) ? n : 0);
  });

  const values = [];
  let month = Number(fromDate?.month);
  let year = Number(fromDate?.year);
  const endMonth = Number(toDate?.month);
  const endYear = Number(toDate?.year);

  while (year < endYear || (year === endYear && month <= endMonth)) {
    values.push(byKey.get(`${year}-${month}`) ?? 0);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return values;
};

export const buildVendorWasteChartDatasets = ({
  payload,
  colors,
  fromDate,
  toDate,
}) => {
  const rows = normalizeVendorWasteSeries(payload);
  if (!rows.length) return [];

  return rows
    .filter((entry) => Array.isArray(entry?.data) && entry.data.length > 0)
    .map((entry) => {
      const category =
        entry?.categoryType ||
        entry?.category ||
        entry?.wasteCategory ||
        "Unknown";
      const color =
        colors?.find((c) => c.label === category)?.color ||
        "rgba(255, 79, 79, 1)";
      return {
        label: category,
        data: buildAlignedMonthlySeries(entry.data, fromDate, toDate),
        backgroundColor: color,
        borderColor: String(color).replace("0.8", "1"),
        borderWidth: 0.5,
      };
    });
};
